import React, { useState, useEffect } from 'react';
import { Switch, Dropdown, Modal, Button, Alert } from 'antd';
import type { IAdBudgetDisplay } from 'types/marketing';
import type { BudgetStatusDisplay } from 'hooks/marketing/useBudgetInfo';
import { DefaultSpecialtyBid, MentalHealthBid } from 'hooks/marketing/useBudgetInfo';
import { formatUTCTime } from 'utils/marketing';
import StrategyTable from 'pages/ProListingSetup/components/StrategyTable';
import SpecialtyBidOverrides from 'pages/ProListingSetup/components/SpecialtyBidOverrides';
import ConfirmSaveModal from 'pages/ProListingSetup/components/ConfirmSaveModal';
import PauseProListingsModal from 'pages/dashboard/Marketing/components/PauseProListingsModal';
import PaymentContent from 'components/PaymentContent';
import WebinarBooking from 'components/WebinarBooking';
import StripeIcon from 'assets/common/stripe.png';
import useProviderHomeInfo from 'hooks/useProviderHomeInfo';
import useGetOffForProlisting from 'hooks/useGetOffForProlisting';
import { CALENDLY_LINK } from 'constants/common';
import { EWebinarScheduleType } from 'types/common';
import StickyFooterActions from './StickyFooterActions';
import s from './s.module.less';

type Props = {
    hasJoinedProListing: boolean;
    isPaused: boolean;
    isReadOnly: boolean;
    isGroupPractice: boolean;
    budgetInfo: IAdBudgetDisplay;
    budgetStatus: BudgetStatusDisplay;
    refetchBudget: () => void;
};

const CampaignSetup: React.FC<Props> = ({
    hasJoinedProListing,
    isPaused,
    isReadOnly,
    isGroupPractice,
    budgetInfo,
    budgetStatus,
    refetchBudget,
}) => {
    const isPreLaunch = !hasJoinedProListing;
    const [homeData] = useProviderHomeInfo();
    const [off] = useGetOffForProlisting();
    const isPPAProvider = !!homeData?.ppaProvider;

    const [strategyData, setStrategyData] = useState<IAdBudgetDisplay>(budgetInfo);
    const [advancedSettings, setAdvancedSettings] = useState(budgetInfo.enableAdvancedSettings || false);

    const [strategyTableErrors, setStrategyTableErrors] = useState<{ bidError: string; budgetError: string }>({
        bidError: '',
        budgetError: '',
    });
    const [specialtyBidErrors, setSpecialtyBidErrors] = useState<Record<string, string>>({});

    const [confirmSaveModalOpen, setConfirmSaveModalOpen] = useState(false);
    const [paymentInfoModalOpen, setPaymentInfoModalOpen] = useState(false);
    const [bidWarningModalOpen, setBidWarningModalOpen] = useState(false);
    const [pauseModalVisible, setPauseModalVisible] = useState(false);
    const [showWebinarBooking, setShowWebinarBooking] = useState(false);

    const { disableBudgetChange } = budgetStatus;

    useEffect(() => {
        if (budgetInfo) {
            setStrategyData(budgetInfo);
            setAdvancedSettings(budgetInfo.enableAdvancedSettings || false);
        }
    }, [budgetInfo]);

    const changeAdvancedSettings = (checked: boolean) => {
        setAdvancedSettings(checked);
        setStrategyData({ ...strategyData, enableAdvancedSettings: checked });
    };

    const getHeading = () => {
        if (isPreLaunch) return 'Set up your Pro-Listings campaign';
        if (isPaused) return 'Your Pro-Listing campaign is paused';
        return 'Manage your Pro-Listings campaign';
    };

    const getSubheading = () => {
        if (isReadOnly && !isGroupPractice) {
            if (isPaused) {
                return 'Your campaign is paused.These settings are managed by your Practice Admin — reach out to them to make changes.';
            } else {
                return 'Your campaign is live.These settings are managed by your Practice Admin — reach out to them to make changes.';
            }
        }
        if (isPreLaunch) {
            return 'Reach more patients by boosting your visibility on Klarity Health. You’re in control — adjust your strategy based on your goals.';
        }
        if (isPaused) {
            return 'Your campaign is paused. Adjust bid, budget, or specialty overrides now — they apply when you resume.';
        }
        return 'Your campaign is live. Tune your bid, budget, and specialty overrides below — changes take effect immediately.';
    };

    const validateForm = () => {
        const hasStrategyErrors = strategyTableErrors.bidError || strategyTableErrors.budgetError;
        const hasSpecialtyErrors = Object.keys(specialtyBidErrors).length > 0;
        return !hasStrategyErrors && !hasSpecialtyErrors;
    };

    const handleSave = () => {
        if (!validateForm() || disableBudgetChange) return;

        if (
            strategyData.specialtyBids?.some(
                (item) =>
                    (item.specialtyName !== 'Psychiatric & Mental Health' && item.bidAmount < DefaultSpecialtyBid) ||
                    (item.specialtyName === 'Psychiatric & Mental Health' && item.bidAmount < MentalHealthBid),
            )
        ) {
            setBidWarningModalOpen(true);
            return;
        }

        if (isPPAProvider || hasJoinedProListing) {
            setConfirmSaveModalOpen(true);
        } else {
            setPaymentInfoModalOpen(true);
        }
    };

    const handleUseRecommendedBid = () => {
        const updatedSpecialtyBids = strategyData.specialtyBids?.map((item) => {
            const recommendedBid =
                item.specialtyName === 'Psychiatric & Mental Health' ? MentalHealthBid : DefaultSpecialtyBid;
            if (item.bidAmount < recommendedBid) {
                return { ...item, bidAmount: recommendedBid };
            }
            return item;
        });
        setStrategyData({ ...strategyData, specialtyBids: updatedSpecialtyBids });
        setBidWarningModalOpen(false);

        if (isPPAProvider || hasJoinedProListing) {
            setConfirmSaveModalOpen(true);
        } else {
            setPaymentInfoModalOpen(true);
        }
    };

    const handleKeepCurrentBid = () => {
        setBidWarningModalOpen(false);
        if (isPPAProvider || hasJoinedProListing) {
            setConfirmSaveModalOpen(true);
        } else {
            setPaymentInfoModalOpen(true);
        }
    };

    const budgetBannerRender = () => {
        let message = '';

        if (disableBudgetChange) {
            if (budgetStatus.isCostAllBudget) {
                message = `Your Pro-Listings have been paused since ${formatUTCTime(budgetInfo.updatedAt)} due to insufficient budget. Increase your budget to resume visibility on Klarity Health. Note: Bid updates are limited to once every 24 hours. Your current bid is active, please return later to make changes.`;
            } else if (budgetStatus.isBudgetNearlyExhausted) {
                message =
                    'You’ve used over 80% of your budget. Increase your budget to stay visible and competitive on Klarity Health. Note: Bid updates are limited to once every 24 hours. Your current bid is already active, and because rankings on Klarity Health aren’t updated in real time, please return after 24 hours to make further changes.';
            } else {
                message =
                    'Bid updates are limited to once every 24 hours. Your current bid is already active, and because rankings on Klarity Health aren’t updated in real time, please return after 24 hours to make further changes.';
            }
        } else if (budgetStatus.isCostAllBudget) {
            message = `Your Pro-Listings have been paused since ${formatUTCTime(budgetInfo.updatedAt)} due to insufficient budget. Increase your budget to resume visibility on Klarity Health.`;
        } else if (budgetStatus.isBudgetNearlyExhausted) {
            message =
                'You’ve used over 80% of your budget. Increase your budget to stay visible and competitive on Klarity Health.';
        } 

        return (
            message && (
                <Alert
                    className={s.budgetBanner}
                    message={message}
                    type="info"
                    showIcon
                    icon={
                        <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 20 20" fill="none">
                            <path
                                fillRule="evenodd"
                                clipRule="evenodd"
                                d="M8.25706 3.09858C9.02167 1.73928 10.9788 1.73928 11.7434 3.09858L17.3237 13.0191C18.0736 14.3523 17.1102 15.9996 15.5805 15.9996H4.4199C2.89025 15.9996 1.92682 14.3523 2.67675 13.0191L8.25706 3.09858ZM11.0001 12.9998C11.0001 13.552 10.5524 13.9998 10.0001 13.9998C9.44784 13.9998 9.00012 13.552 9.00012 12.9998C9.00012 12.4475 9.44784 11.9998 10.0001 11.9998C10.5524 11.9998 11.0001 12.4475 11.0001 12.9998ZM10.0001 4.99976C9.44784 4.99976 9.00012 5.44747 9.00012 5.99976V8.99976C9.00012 9.55204 9.44784 9.99976 10.0001 9.99976C10.5524 9.99976 11.0001 9.55204 11.0001 8.99976V5.99976C11.0001 5.44747 10.5524 4.99976 10.0001 4.99976Z"
                                fill="#FBBF24"
                            />
                        </svg>
                    }
                />
            )
        );
    };

    return (
        <div className={s.setupWrap}>
            {/* Live banner */}
            {hasJoinedProListing && !isPaused && !budgetStatus.isCostAllBudget && (
                <div className={s.liveBanner}>
                    <div className={s.liveBannerIcon}>&#10003;</div>
                    <div className={s.liveBannerContent}>
                        <div className={s.liveBannerTitle}>Your Pro-Listing campaign is live</div>
                        <div className={s.liveBannerText}>
                            {isReadOnly
                                ? 'Bidding is active for your listing. Reach out to your Practice Admin to adjust the campaign.'
                                : 'Bidding is active for your listing. Adjust your strategy anytime below.'}
                        </div>
                    </div>
                    {!isReadOnly && (
                        <Dropdown
                            menu={{
                                items: [{ key: 'pause', label: 'Pause campaign' }],
                                onClick: ({ key }) => {
                                    if (key === 'pause') {
                                        setPauseModalVisible(true);
                                    }
                                },
                            }}
                            trigger={['click']}
                            placement="bottomRight"
                        >
                            <button type="button" className={s.adjustBtn}>
                                Adjust settings
                                <svg
                                    width="10"
                                    height="10"
                                    viewBox="0 0 12 12"
                                    fill="none"
                                    stroke="currentColor"
                                    strokeWidth="1.6"
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                >
                                    <path d="M3 4.5l3 3 3-3" />
                                </svg>
                            </button>
                        </Dropdown>
                    )}
                </div>
            )}

            {/* Paused banner */}
            {isPaused && (
                <div className={s.pausedBanner}>
                    <div className={s.pausedBannerInner}>
                        <svg width="20" height="20" viewBox="0 0 20 20" fill="none" style={{ flexShrink: 0 }}>
                            <circle cx="10" cy="10" r="8.5" fill="#FBBF24" />
                            <path d="M10 6v4.5" stroke="#fff" strokeWidth="1.8" strokeLinecap="round" />
                            <circle cx="10" cy="13.5" r="1" fill="#fff" />
                        </svg>
                        <p className={s.pausedBannerText}>
                            Your Pro-Listing was paused on <strong>{formatUTCTime(budgetInfo.updatedAt)}</strong>. You
                            can resume it anytime to continue reaching more patients.
                        </p>
                    </div>
                </div>
            )}

            {/* Heading */}
            <div className={s.headingGroup}>
                <h2 className={s.setupHeading}>{getHeading()}</h2>
                <p className={s.setupSubheading}>{getSubheading()}</p>
            </div>

            {/* Free credit alert */}
            {isPreLaunch && (
                <Alert
                    type="info"
                    className={s.creditAlert}
                    message={
                        <span>
                            &#127873; Your ${off} Pro-Listings free credit is active and will cover your first campaign
                            costs.
                        </span>
                    }
                />
            )}

            {/* Strategy card */}
            <div className={s.strategyCard}>
                <div className={s.strategyCardHeader}>
                    <div className={s.strategyTitle}>Set your campaign strategy</div>
                    <div className={s.strategySubtitle}>
                        Boost your visibility with competitive bids and stay in control with a weekly or monthly budget
                        cap.
                    </div>
                </div>
                {budgetBannerRender()}
                <StrategyTable
                    data={strategyData}
                    onChange={(d) => setStrategyData((prev) => ({ ...prev, ...d }))}
                    onValidationChange={setStrategyTableErrors}
                    isWarning={budgetStatus.isBudgetNearlyExhausted || budgetStatus.isCostAllBudget}
                    disabled={isReadOnly || disableBudgetChange}
                />
            </div>

            {/* Advanced settings */}
            <div className={s.advancedCard}>
                <div className={s.advancedHeader}>
                    <div className={s.advancedHeaderLeft}>
                        <div className={s.advancedTitle}>Advanced settings</div>
                        <div className={s.advancedSubtitle}>
                            Review how your budget is distributed and override bids for high-value specialties.
                        </div>
                    </div>
                    <Switch
                        checked={advancedSettings}
                        onChange={changeAdvancedSettings}
                        disabled={isReadOnly || disableBudgetChange}
                    />
                </div>
                {advancedSettings && (
                    <div className={s.advancedPanel}>
                        {/* Specialty overrides */}
                        <SpecialtyBidOverrides
                            specialtyBids={strategyData.specialtyBids}
                            bidPerBooking={strategyData.generalBidAmount}
                            budgetCap={strategyData.budgetCap || 0}
                            budgetPeriod={strategyData.budgetPeriod}
                            onChange={(bids) => setStrategyData((prev) => ({ ...prev, specialtyBids: bids }))}
                            onValidationChange={setSpecialtyBidErrors}
                            disabled={isReadOnly || disableBudgetChange}
                        />
                    </div>
                )}
            </div>

            {/* Footer actions */}
            {!isReadOnly && (
                <StickyFooterActions>
                    <Button onClick={() => setShowWebinarBooking(true)}>Talk to a Pro-Listings specialist</Button>
                    <Button type="primary" onClick={handleSave} disabled={disableBudgetChange}>
                        {isPreLaunch ? 'Launch campaign' : 'Save changes'}
                    </Button>
                </StickyFooterActions>
            )}

            {/* Confirm save modal */}
            <ConfirmSaveModal
                open={confirmSaveModalOpen}
                onCancel={() => setConfirmSaveModalOpen(false)}
                strategyData={strategyData}
                onConfirm={() => {
                    refetchBudget();
                }}
            />

            {/* Payment info modal */}
            <Modal
                open={paymentInfoModalOpen}
                onCancel={() => setPaymentInfoModalOpen(false)}
                footer={null}
                width={560}
                title={
                    <div className="flex items-center gap-2">
                        Add payment information <img src={StripeIcon} alt="stripe" className={s.stripeIcon} />
                    </div>
                }
                destroyOnClose
            >
                <div className={s.paymentInfoDesc}>
                    Enter your payment details to start Pro-Listings. You&apos;ll only be charged when a new patient
                    books through your listing.
                </div>
                <PaymentContent
                    confirmButtonText="Continue to confirmation"
                    onSuccess={() => {
                        setPaymentInfoModalOpen(false);
                        setConfirmSaveModalOpen(true);
                    }}
                    hidePolicy
                    isPPV
                />
            </Modal>

            {/* Bid warning modal */}
            <Modal
                open={bidWarningModalOpen}
                onCancel={() => setBidWarningModalOpen(false)}
                footer={null}
                width={560}
                centered
                className={s.bidWarningModal}
            >
                <div className={s.bidWarningContent}>
                    <div className={s.warningIconWrapper}>
                        <svg xmlns="http://www.w3.org/2000/svg" width="40" height="40" viewBox="0 0 40 40" fill="none">
                            <path
                                fillRule="evenodd"
                                clipRule="evenodd"
                                d="M16.5161 6.19911C18.0453 3.48051 21.9595 3.4805 23.4887 6.19911L34.6493 26.0402C36.1491 28.7066 34.2223 32.0012 31.163 32.0012H8.84176C5.78246 32.0012 3.8556 28.7066 5.35546 26.0402L16.5161 6.19911ZM22.0022 26.0015C22.0022 27.106 21.1068 28.0015 20.0022 28.0015C18.8976 28.0015 18.0022 27.106 18.0022 26.0015C18.0022 24.8969 18.8976 24.0015 20.0022 24.0015C21.1068 24.0015 22.0022 24.8969 22.0022 26.0015ZM20.0022 10.0015C18.8976 10.0015 18.0022 10.8969 18.0022 12.0015V18.0015C18.0022 19.106 18.8976 20.0015 20.0022 20.0015C21.1068 20.0015 22.0022 19.106 22.0022 18.0015V12.0015C22.0022 10.8969 21.1068 10.0015 20.0022 10.0015Z"
                                fill="#FBBF24"
                            />
                        </svg>
                    </div>
                    <h3 className={s.warningTitle}>Your bid is below the recommended range</h3>
                    <div className={s.warningDescription}>
                        <p>
                            Lower bids may reduce your visibility in patient searches. Providers with competitive bids
                            see up to <strong>3X more bookings</strong>. Keep your bid at the recommended level to stay
                            competitive.
                        </p>
                    </div>
                    <div className={s.warningButtons}>
                        <Button type="primary" size="large" block onClick={handleUseRecommendedBid}>
                            Use recommended bid
                        </Button>
                        <Button size="large" block onClick={handleKeepCurrentBid}>
                            Keep current bid
                        </Button>
                    </div>
                </div>
            </Modal>

            {/* Pause modal */}
            <PauseProListingsModal
                visible={pauseModalVisible}
                onCancel={() => setPauseModalVisible(false)}
                onConfirm={() => {
                    refetchBudget();
                }}
            />

            {/* Webinar booking */}
            <WebinarBooking
                modalHeader={
                    <div className={s.wHeader}>
                        <div className={s.wTitle}>Talk to a Pro-Listings specialist</div>
                        <div className={s.wDesc}>
                            Want to grow faster with pro listings? Book a time to speak with our specialist and get
                            personalized guidance.
                        </div>
                    </div>
                }
                show={showWebinarBooking}
                link={CALENDLY_LINK.PRO_LISTINGS_SPECIALIST}
                webinarScheduleType={EWebinarScheduleType.PRO_LISTINGS_SPECIALIST}
                onClose={() => {
                    setShowWebinarBooking(false);
                }}
            />
        </div>
    );
};

export default CampaignSetup;
